import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MapPin, Volume2, VolumeX, Play, Pause, Shield, Globe, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import heroImage from "@/assets/hero-villa.jpg";

const HERO_VIDEOS = [
  {
    url: "https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4",
    location: "Monaco"
  },
  {
    url: "https://videos.pexels.com/video-files/3015510/3015510-hd_1920_1080_24fps.mp4",
    location: "Mediterranean"
  },
  {
    url: "https://videos.pexels.com/video-files/3214472/3214472-uhd_2560_1440_25fps.mp4",
    location: "Italian Coast"
  }
];

const stats = [
  { value: "14", label: "Global Markets", icon: Globe },
  { value: "76+", label: "Curated Properties", icon: MapPin },
  { value: "€2M–50M+", label: "Investment Range", icon: TrendingUp },
  { value: "140", label: "Years of Legacy", icon: Shield },
];

export const HeroSection = () => {
  const [currentVideo, setCurrentVideo] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [useVideo] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Video Loading Shimmer */}
      {useVideo && !videoLoaded && (
        <div className="absolute inset-0 bg-charcoal z-[1]">
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
        </div>
      )}

      {/* Video Background */}
      {useVideo && (
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            onCanPlayThrough={() => setVideoLoaded(true)}
            onEnded={() => {
              setCurrentVideo((prev) => (prev + 1) % HERO_VIDEOS.length);
              setVideoLoaded(false);
            }}
            className={`w-full h-full object-cover transition-opacity duration-1000 ${
              videoLoaded ? "opacity-100" : "opacity-0"
            }`}
          >
            <source src={HERO_VIDEOS[currentVideo].url} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/50 via-charcoal/30 to-charcoal/70" />
          <div className="absolute inset-0 bg-charcoal/20" />
        </div>
      )}

      {/* Fallback Image */}
      <motion.div
        className={`absolute inset-0 transition-opacity duration-1000 ${
          useVideo && videoLoaded ? "opacity-0" : "opacity-100"
        }`}
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 20, ease: "linear" }}
      >
        <img
          src={heroImage}
          alt="Luxury Mediterranean villa with infinity pool overlooking the sea at sunset"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 bg-charcoal/30" />
      </motion.div>

      {/* Video Controls */}
      {useVideo && videoLoaded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-6 right-8 z-20 flex items-center gap-2"
        >
          <button
            onClick={togglePlay}
            className="p-2 rounded-full bg-charcoal/40 backdrop-blur-sm border border-cream/20 text-cream/70 hover:text-cream hover:border-cream/40 transition-all duration-300"
            aria-label={isPlaying ? "Pause video" : "Play video"}
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <button
            onClick={toggleMute}
            className="p-2 rounded-full bg-charcoal/40 backdrop-blur-sm border border-cream/20 text-cream/70 hover:text-cream hover:border-cream/40 transition-all duration-300"
            aria-label={isMuted ? "Unmute video" : "Mute video"}
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        </motion.div>
      )}

      {/* Corner Accents */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-8 left-8 w-24 h-24 border-l border-t border-cream/20" />
        <div className="absolute top-8 right-8 w-24 h-24 border-r border-t border-cream/20" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-6 lg:px-12 pt-32 pb-8">
        <div className="max-w-4xl">
          {/* Trust Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-3 px-5 py-2.5 border border-cream/25 bg-charcoal/30 backdrop-blur-sm rounded-full">
              <span className="w-2 h-2 rounded-full bg-gold" />
              <span className="font-body text-xs uppercase tracking-[0.25em] text-cream/80">
                Your Global Real Estate Advisory Partner
              </span>
            </div>
          </motion.div>

          {/* Headline — purpose-driven */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light text-cream leading-[1.05] mb-6"
          >
            <span className="block">We Guide Your</span>
            <span className="block italic font-medium text-gold-light mt-1">
              Property Investment
            </span>
            <span className="block mt-1">Worldwide.</span>
          </motion.h1>

          {/* Value Proposition */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="font-body text-base sm:text-lg md:text-xl text-cream/75 max-w-2xl mb-10 leading-relaxed"
          >
            From first inquiry to key handover — we connect you with the best local 
            brokerages across 14 global markets, managing every step of your 
            acquisition with fiduciary precision.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="flex flex-col sm:flex-row gap-4 mb-16"
          >
            <Button variant="hero-solid" size="xl" asChild className="group">
              <a href="#investment-assessment" className="flex items-center gap-2">
                <span>Start Your Investment Journey</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
            <Button variant="hero" size="xl" asChild>
              <a href="#contact">Schedule Private Consultation</a>
            </Button>
          </motion.div>
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-0 md:divide-x md:divide-cream/15 bg-charcoal/40 backdrop-blur-md border border-cream/10 rounded-xl p-4 md:p-0 max-w-4xl"
        >
          {stats.map((stat, i) => (
            <div key={stat.label} className="flex items-center gap-3 md:px-6 md:py-5">
              <stat.icon className="h-5 w-5 text-gold flex-shrink-0" />
              <div>
                <p className="font-display text-xl md:text-2xl text-cream font-medium leading-none">{stat.value}</p>
                <p className="font-body text-[11px] text-cream/50 uppercase tracking-wider mt-1">{stat.label}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
